import { useState, useMemo, useEffect, useRef } from "react";

import { filterByCategory } from "@/shared/constants/estateCategories";
import { useQuery } from "@tanstack/react-query";

import { usePerson } from "@/shared/hooks/useAppContext";
import { useBenefitsTaskAnswers } from "@/shared/hooks/useBenefitsTaskAnswers";
import { useDeceasedPassportData } from "@/shared/hooks/useDeceasedPassportData";
import { useDeceasedSSNData } from "@/shared/hooks/useDeceasedSSNData";
import { useDeceasedUserData } from "@/shared/hooks/useDeceasedUserData";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { useRegularSSNData } from "@/shared/hooks/useRegularSSNData";
import { useUserIdExtraction } from "@/shared/hooks/useUserIdExtraction";
import { useUserRelationship } from "@/shared/hooks/useUserRelationship";
import { useUserSettingsData } from "@/shared/hooks/useUserSettingsData";
import { supabase } from "@/shared/lib/supabase";
import { resolveFieldValue, type ExtraFieldSources } from "@/shared/utils/essentialsFieldSources";

import type { EssentialModule } from "@/shared/types/estateEssentials";
import type { EstateItem } from "@/shared/types/estateTypes";

interface ResolvedInitialDataResult {
  initialData: Record<string, string> | undefined;
  isReady: boolean;
  shouldAutoOpen: boolean;
  isCompleted: boolean;
  taskStatus: string | null;
  deceasedData: ReturnType<typeof useDeceasedUserData>;
  userData: ReturnType<typeof useUserSettingsData>;
  relationship: string | undefined;
  vehicleItems: EstateItem[];
  assets: ReturnType<typeof useEstateItems>["assets"];
  updateEstateItem: ReturnType<typeof useEstateItems>["updateItem"];
  addEstateItem: ReturnType<typeof useEstateItems>["addItem"];
}

export function useResolvedInitialData(
  module: EssentialModule | undefined,
  isVehicleModule: boolean,
): ResolvedInitialDataResult {
  const person = usePerson();
  const deceasedData = useDeceasedUserData();
  const userData = useUserSettingsData();
  const idData = useUserIdExtraction();
  const benefitsData = useBenefitsTaskAnswers();
  const { deceasedSSN, isLoading: isDeceasedSSNLoading } = useDeceasedSSNData();
  const { deceasedPassportNumber, isLoading: isPassportLoading } = useDeceasedPassportData();
  const { ssn: userSSN, isLoading: isUserSSNLoading } = useRegularSSNData();
  const { relationship, isLoading: isRelationshipLoading } = useUserRelationship();

  const taskId = module?.taskId ?? null;
  const estateId = person?.estate_id ?? null;

  const { data: savedTask, isLoading: isSavedAnswersLoading } = useQuery({
    queryKey: ["estateTaskAnswers", estateId, taskId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("estate_tasks")
        .select("answers, status")
        .eq("estate_id", estateId!)
        .eq("task_id", taskId!)
        .limit(1);
      if (error) throw error;
      const row = data?.[0];
      return {
        answers: (row?.answers ?? {}) as Record<string, string>,
        status: (row?.status ?? null) as string | null,
      };
    },
    enabled: !!estateId && !!taskId,
  });

  const {
    assets,
    isLoading: isEstateItemsLoading,
    isFetching: isEstateItemsFetching,
    updateItem: updateEstateItem,
    addItem: addEstateItem,
  } = useEstateItems(isVehicleModule ? deceasedData.deceasedUserId : null);

  const vehicleItems = useMemo(() => filterByCategory(assets ?? [], "Vehicles"), [assets]);

  const userDOB = userData.dateOfBirth ? userData.dateOfBirth.toISOString().split("T")[0] : "";

  const extraSources: ExtraFieldSources = useMemo(
    () => ({
      benefits: benefitsData,
      relationship,
      deceasedSSN,
      deceasedPassportNumber,
      userSSN,
      userDOB,
    }),
    [benefitsData, relationship, deceasedSSN, deceasedPassportNumber, userSSN, userDOB],
  );

  const [result, setResult] = useState<{
    initialData: Record<string, string> | undefined;
    isReady: boolean;
    shouldAutoOpen: boolean;
    isCompleted: boolean;
  }>({ initialData: undefined, isReady: false, shouldAutoOpen: false, isCompleted: false });

  const hasResolvedWithData = useRef(false);

  const allLoaded =
    !!module &&
    deceasedData.hasPersonData &&
    !deceasedData.isLoading &&
    !userData.isLoading &&
    !isDeceasedSSNLoading &&
    !isPassportLoading &&
    !isUserSSNLoading &&
    !benefitsData.isLoading &&
    !isRelationshipLoading &&
    !isSavedAnswersLoading &&
    (!isVehicleModule ||
      (!isEstateItemsLoading && !isEstateItemsFetching && !!deceasedData.deceasedUserId));

  useEffect(() => {
    if (!allLoaded || !module?.formFields) return;
    if (hasResolvedWithData.current) return;

    const initial: Record<string, string> = {};
    for (const field of module.formFields) {
      const resolved = resolveFieldValue(
        field.autoPopulateKey,
        deceasedData,
        userData,
        idData,
        extraSources,
      );
      if (resolved) {
        initial[field.key] = resolved;
      } else if (field.type === "radio" && field.options?.length) {
        const firstOpt = field.options[0];
        initial[field.key] = typeof firstOpt === "string" ? firstOpt : firstOpt.value;
      } else if (field.type === "stepper") {
        initial[field.key] = "1";
      } else {
        initial[field.key] = "";
      }
    }

    if (savedTask?.answers) {
      for (const [key, value] of Object.entries(savedTask.answers)) {
        if (value) initial[key] = value;
      }
    }

    if (isVehicleModule && vehicleItems.length > 0) {
      initial.vehicle_count = String(vehicleItems.length);
      const savedSelectedId = savedTask?.answers?.selected_vehicle_id;
      const firstId = String(vehicleItems[0].id);
      initial.selected_vehicle_id = savedSelectedId
        ? vehicleItems.find((v) => String(v.id) === savedSelectedId)
          ? savedSelectedId
          : firstId
        : firstId;
    }

    hasResolvedWithData.current = true;
    setResult({
      initialData: initial,
      isReady: true,
      shouldAutoOpen: !savedTask?.status || savedTask.status === "not_started",
      isCompleted: savedTask?.status === "completed",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allLoaded]);

  return {
    ...result,
    taskStatus: savedTask?.status ?? null,
    deceasedData,
    userData,
    relationship,
    vehicleItems,
    assets,
    updateEstateItem,
    addEstateItem,
  };
}
